import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import {
  getAgencyHierarchy,
  buildHierarchyTree,
  type HierarchyTreeNode,
} from "../services/agency-hierarchy";

const hierarchyKeys = {
  // Range is part of the key so switching the dashboard's date filter refetches rather
  // than showing a cached window with the new label.
  tree: (agencyId: string, start?: string, end?: string, period?: string) =>
    [
      "agencies",
      "hierarchy",
      agencyId,
      start ?? "default",
      end ?? "default",
      period ?? "default",
    ] as const,
};

/**
 * The org tree for `agencyId`, already nested and rolled up.
 *
 * staleTime is 5 minutes: an org chart changes when someone creates a sub-agency or
 * moves an agent, not second to second. Refetching on every tab focus would hammer a
 * recursive CTE for data that is almost never different.
 */
export function useAgencyHierarchy(
  agencyId?: string,
  range?: { startDate?: string; endDate?: string; period?: string },
) {
  const query = useQuery({
    queryKey: hierarchyKeys.tree(
      agencyId ?? "",
      range?.startDate,
      range?.endDate,
      range?.period,
    ),
    queryFn: () => getAgencyHierarchy(agencyId as string, range),
    enabled: !!agencyId,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const tree = useMemo<HierarchyTreeNode[]>(
    () => buildHierarchyTree(query.data ?? []),
    [query.data],
  );

  const summary = useMemo(() => {
    const rows = query.data ?? [];
    return {
      agencies: rows.length,
      // ⚠️ Deliberately NO org-wide agent count here. Agent Count on the dashboard above
      // already answers that, and having two headcounts made this strip disagree with it
      // three different ways in one sitting (5, then 27, then 37) depending on whether it
      // read the root row, the server rollup, or a sum of per-agency counts. Per-agency
      // headcount still shows on each card in the chart, where it is unambiguous.
      sold: rows.reduce((n, r) => n + r.soldPremium, 0),
      underwriting: rows.reduce((n, r) => n + r.underwritingPremium, 0),
      production: rows.reduce((n, r) => n + r.production, 0),
      // Depth is 0-based, so a flat org (root only) reads as 1 level, not 0.
      depth: rows.length ? Math.max(...rows.map((r) => r.depth)) + 1 : 0,
    };
  }, [query.data]);

  return { ...query, tree, summary };
}
